import { useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { trackPageview } from './lib/analytics'
import MainLayout from './layouts/MainLayout'
import HomePage from './pages/HomePage'
import AboutPage from './pages/AboutPage'
import StrategyPage from './pages/StrategyPage'
import TrackRecordPage from './pages/TrackRecordPage'
import InvestPage from './pages/InvestPage'
import ContactPage from './pages/ContactPage'
import OperatorQuestionsPage from './pages/OperatorQuestionsPage'
import FitAssessmentPage from './pages/FitAssessmentPage'
import ForInvestorsPage from './pages/ForInvestorsPage'
import ResourcesPage from './pages/resources/ResourcesPage'
import UnderwritingBasicsPage from './pages/resources/UnderwritingBasicsPage'
import FinancingOptionsPage from './pages/resources/FinancingOptionsPage'
import DealCriteriaPage from './pages/resources/DealCriteriaPage'
import DueDiligenceChecklistPage from './pages/resources/DueDiligenceChecklistPage'
import PropertyComparisonSheetPage from './pages/resources/PropertyComparisonSheetPage'
import RentalCalculatorPage from './pages/resources/RentalCalculatorPage'
import ClevelandOverviewPage from './pages/resources/ClevelandOverviewPage'
import CalculatorsPage from './pages/calculators/CalculatorsPage'
import MortgagePaymentCalculator from './pages/calculators/MortgagePaymentCalculator'
import CashOnCashCalculator from './pages/calculators/CashOnCashCalculator'
import DscrCalculator from './pages/calculators/DscrCalculator'
import RentVsBuyCalculator from './pages/calculators/RentVsBuyCalculator'
import BrrrrCalculator from './pages/calculators/BrrrrCalculator'
import NotFoundPage from './pages/NotFoundPage'

// Pages are imported EAGERLY, not via lazy(). renderToString does not resolve
// lazy boundaries — it renders the Suspense fallback — so prerendering with
// lazy routes would ship 24 pages each saying "Loading...", which is worse than
// the empty shell it replaced because it looks like it worked.

/**
 * Emits a GA4 page_view on each client-side route change.
 *
 * Lives here rather than in main.tsx because it calls useLocation and therefore
 * needs router context, and mountClient owns the router now. Safe under SSR: the
 * effect never runs server-side, so prerendering emits no pageview.
 */
function AnalyticsTracker() {
  const location = useLocation()
  useEffect(() => {
    trackPageview(location.pathname + location.search)
  }, [location.pathname, location.search])
  return null
}

export default function App() {
  return (
    <>
      <AnalyticsTracker />
      <Routes>
        <Route element={<MainLayout />}>
          {/* Main pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/strategy" element={<StrategyPage />} />
          <Route path="/track-record" element={<TrackRecordPage />} />
          <Route path="/invest" element={<InvestPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* For Investors */}
          <Route path="/for-investors" element={<ForInvestorsPage />} />
          <Route path="/operator-questions" element={<OperatorQuestionsPage />} />
          <Route path="/fit" element={<FitAssessmentPage />} />

          {/* Resources */}
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/resources/underwriting-basics" element={<UnderwritingBasicsPage />} />
          <Route path="/resources/financing-options" element={<FinancingOptionsPage />} />
          <Route path="/resources/deal-criteria" element={<DealCriteriaPage />} />
          <Route path="/resources/due-diligence-checklist" element={<DueDiligenceChecklistPage />} />
          <Route path="/resources/property-comparison-sheet" element={<PropertyComparisonSheetPage />} />
          <Route path="/resources/rental-calculator" element={<RentalCalculatorPage />} />
          <Route path="/resources/cleveland-overview" element={<ClevelandOverviewPage />} />

          {/* Calculators */}
          <Route path="/resources/calculators" element={<CalculatorsPage />} />
          <Route path="/resources/calculators/mortgage-payment" element={<MortgagePaymentCalculator />} />
          <Route path="/resources/calculators/cash-on-cash" element={<CashOnCashCalculator />} />
          <Route path="/resources/calculators/dscr" element={<DscrCalculator />} />
          <Route path="/resources/calculators/rent-vs-buy" element={<RentVsBuyCalculator />} />
          <Route path="/resources/calculators/brrrr" element={<BrrrrCalculator />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>
  )
}
